import './Graben.css';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSortedTheses, getLeadGap } from './grabenData';
import vorData from './vorData.json';

// ── Axis geometry ──────────────────────────────────────────────────
const EDGE_INSET = 5; // % from each edge so extreme values don't clip

function pct(value) {
  return EDGE_INSET + (value / 100) * (100 - 2 * EDGE_INSET);
}

// ── CohortDot ──────────────────────────────────────────────────────
export function CohortDot({ color, size = 12 }) {
  return (
    <span
      className="grb-dot"
      style={{ width: size, height: size, background: color }}
    />
  );
}

// ── OpinionAxis ────────────────────────────────────────────────────
export function OpinionAxis({ points }) {
  const DOT = 11;
  const sorted = [...points].sort((a, b) => a.value - b.value);
  const placed = sorted.map((p, i) => ({
    ...p,
    left: pct(p.value),
    isAbove: i % 2 === 1,
  }));

  const lefts = placed.map(p => p.left);
  const minLeft = Math.min(...lefts);
  const maxLeft = Math.max(...lefts);

  return (
    <div className="grb-axis">
      <div className="grb-axis-track" />

      {placed.length > 1 && (
        <div
          className="grb-axis-bridge"
          style={{ left: `${minLeft}%`, width: `${maxLeft - minLeft}%` }}
        />
      )}

      {placed.map(p => (
        <React.Fragment key={p.id}>
          <span
            className={`grb-axis-leader grb-axis-leader--${p.isAbove ? 'above' : 'below'}`}
            style={{ left: `${p.left}%` }}
          />
          <span
            className="grb-axis-dot"
            style={{
              left:       `calc(${p.left}% - ${DOT / 2}px)`,
              top:        `calc(50% - ${DOT / 2}px)`,
              width:      DOT,
              height:     DOT,
              background: p.color,
            }}
          />
          {p.label && (
            <span
              className={`grb-axis-label grb-axis-label--${p.isAbove ? 'above' : 'below'}`}
              style={{ left: `${p.left}%`, color: p.color }}
            >
              {p.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── ThesisRow ──────────────────────────────────────────────────────
function ThesisRow({ thesis, thesisText, lang, rank, onTap }) {
  const points = thesis.cohorts.map(c => ({
    id:    c.id,
    value: c.value,
    color: c.color,
    label: c.labelShort?.[lang] || c.labelShort?.en || '',
  }));
  return (
    <button className="grb-row" onClick={onTap}>
      <div className="grb-row-head">
        <span className="grb-row-arc">
          <svg width="30" height="30" viewBox="0 0 40 40" className="grb-row-arc-svg">
            <path d="M 32.95 21.13 A 13 13 0 1 1 20 7" stroke="#3B7CB8" strokeWidth="3.2"
              fill="none" strokeLinecap="round" />
            <text x="20" y="20" textAnchor="middle" dominantBaseline="central"
              fill="#3B7CB8" fontSize="15" fontWeight="600" fontFamily="'DM Sans', sans-serif">{rank}</text>
          </svg>
        </span>
        <p className="grb-row-body">{thesisText}</p>
        <svg className="grb-row-chevron" width="14" height="14" viewBox="0 0 16 16" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 3l5 5-5 5"/>
        </svg>
      </div>
      <div className="grb-row-axis">
        <OpinionAxis points={points} />
      </div>
    </button>
  );
}

// ── Graben ─────────────────────────────────────────────────────────
export default function Graben() {
  const history        = useHistory();
  const { t, i18n }    = useTranslation();
  const lang           = i18n.language?.slice(0, 2) || 'en';

  const sorted  = getSortedTheses();
  const leadGap = getLeadGap();

  const getThesisText = (id) => {
    const entry = vorData.theses.find(t => t.id === id);
    if (!entry) return id;
    if (lang === 'de') return entry.text_de;
    if (lang === 'fr') return entry.text_fr;
    return entry.text_en;
  };

  const getTopicTitle = (topic) => {
    if (lang === 'de') return topic.title_de;
    if (lang === 'fr') return topic.title_fr;
    return topic.title_en;
  };

  // Group the gap-sorted theses under their topic, preserving topic order.
  const topicOf = Object.fromEntries(vorData.theses.map(th => [th.id, th.topicId]));
  const groups = vorData.topics
    .map(topic => ({
      topic,
      theses: sorted.filter(th => topicOf[th.id] === topic.id),
    }))
    .filter(g => g.theses.length > 0);

  return (
    <div className="grb-root">
      {/* Scrollable body */}
      <div className="grb-scroll">

        <button className="grb-back" onClick={() => history.goBack()}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3L5 8l5 5" />
          </svg>
          {t('td.back')}
        </button>

        {/* Eyebrow + title */}
        <div className="grb-intro">
          <span className="grb-eyebrow">Standing measurement · Live</span>
          <h1 className="grb-title">Where Munich Voices currently stand</h1>
        </div>

        {/* Editorial insight block */}
        <div className="grb-insight">
          <span className="grb-insight-label">Largest gap</span>
          <div className="grb-insight-row">
            <span className="grb-insight-num">{leadGap}</span>
            <div className="grb-insight-aside">
              <span className="grb-insight-unit">points</span>
              <p className="grb-insight-body">
                Opinions diverge most on support for Ukraine - between those in the room and the general public.
              </p>
            </div>
          </div>
        </div>

        {/* Scale header */}
        <div className="grb-scale-header">
          <span className="grb-scale-label">Disagree</span>
          <span className="grb-scale-label">Agree</span>
        </div>

        {/* Thesis list, grouped by topic */}
        <div className="grb-list">
          {groups.map(({ topic, theses }) => (
            <section key={topic.id} className="grb-group">
              <h2 className="grb-group-title">{getTopicTitle(topic)}</h2>
              {theses.map((thesis, idx) => (
                <ThesisRow
                  key={thesis.id}
                  thesis={thesis}
                  thesisText={getThesisText(thesis.id)}
                  lang={lang}
                  rank={idx + 1}
                  onTap={() => history.push(`/thesis/${thesis.id}`)}
                />
              ))}
            </section>
          ))}
        </div>

        <p className="grb-copyright">Prototype by Ferdinand Riedl · riedlsoftware.com</p>
      </div>
    </div>
  );
}
